import { createSlice } from '@reduxjs/toolkit';
import {
  AddScooterNoteParams,
  GetScooterNotesParams,
  GetScootersParams,
  Scooter,
  UpdateScooterInfoBody
} from '../../resources/types/scootersTypes';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { handleDefaultErrors } from '../../resources/functions';

export interface ScootersState {
  items: Scooter[];
  total: number;
  free: number;
  onContract: number;
  transporting: number;
  charging: number;
  maintenance: number;
  offline: number;
  unlocked: number;
  lowBattery: number;
}

const initialState: ScootersState = {
  items: [],
  total: 0,
  free: 0,
  onContract: 0,
  transporting: 0,
  charging: 0,
  maintenance: 0,
  offline: 0,
  unlocked: 0,
  lowBattery: 0
};

export const scootersSlice = createSlice({
  name: 'scooters',
  initialState,
  reducers: {
    getScooters: (state, action) => {
      state.items = action.payload.scooters || [];
      
      // Support both formats: counters (WebSocket) and direct data (API)
      const counters = action.payload.counters || {};

      if (counters.total !== undefined) {
        state.total = counters.total ?? 0;
      }
      if (counters.free !== undefined) {
        state.free = counters.free ?? 0;
      }
      if (counters.onContract !== undefined) {
        state.onContract = counters.onContract ?? 0;
      }
      if (counters.transporting !== undefined) {
        state.transporting = counters.transporting ?? 0;
      }
      if (counters.charging !== undefined) {
        state.charging = counters.charging ?? 0;
      }
      if (counters.maintenance !== undefined) {
        state.maintenance = counters.maintenance ?? 0;
      }
      if (counters.offline !== undefined) {
        state.offline = counters.offline ?? 0;
      }
      if (counters.unlocked !== undefined) {
        state.unlocked = counters.unlocked ?? 0;
      }
      if (counters.lowBattery !== undefined) {
        state.lowBattery = counters.lowBattery ?? 0;
      }
    },
    updateScooterStatus: (state, action) => {
      const { id, status } = action.payload;
      const scooterIndex = state.items.findIndex(s => s._id === id);
      if (scooterIndex !== -1) {
        state.items[scooterIndex].status = status;
      }
    },
    updateScooterInfo: (state, action) => {
      const { id, updates } = action.payload;
      const scooterIndex = state.items.findIndex(s => s._id === id);
      if (scooterIndex !== -1) {
        if ('forceCoords' in updates) {
          updates.hasForceCoords = updates.forceCoords !== null;
        }
        state.items[scooterIndex] = {
          ...state.items[scooterIndex],
          ...updates
        };
      }
    }
  }
});

export const { getScooters, updateScooterStatus, updateScooterInfo } = scootersSlice.actions;

export const selectScooters = (state: RootState) => state.scooters;

export const getScootersAsync =
  (params?: GetScootersParams): AppThunk =>
  async dispatch => {
    try {
      const response = await axios.get('/scooters?version=2', { params });
      dispatch(getScooters(response.data));
    } catch (e: any) {
      handleDefaultErrors(e);
    }
  };

export const updateScooterInfoAsync =
  (id: string, body: UpdateScooterInfoBody, success?: () => void): AppThunk =>
  async dispatch => {
    try {
      await axios.put(`/scooters/${id}/info`, body);
      dispatch(updateScooterInfo({ id, updates: body }));
      if (success) {
        success();
      }
    } catch (e: any) {
      handleDefaultErrors(e);
    }
  };

export const updateScooterStatusAsync =
  (id: string, body: UpdateScooterInfoBody, success?: () => void): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(updateScooterStatus({ id, status: body.status }));
      await axios.put(`/scooters/${id}/status`, body);
      
      const admins = getState().admins;
      const params = admins.currentCity 
        ? { cityId: admins.currentCity, limit: 5000 }
        : { limit: 5000 };
      dispatch(getScootersAsync(params));
      
      if (success) {
        success();
      }
    } catch (e: any) {
      handleDefaultErrors(e);
      const admins = getState().admins;
      const params = admins.currentCity 
        ? { cityId: admins.currentCity, limit: 5000 }
        : { limit: 5000 };
      dispatch(getScootersAsync(params));
    }
  };

export const lockScooterAsync =
  (id: string, body: { lock: boolean }): AppThunk =>
  async () => {
    try {
      await axios.put(`/scooters/${id}/locking`, body);
    } catch (e: any) {
      handleDefaultErrors(e);
    }
  };

export const signalScooterAsync =
  (id: string, body: { value: boolean }): AppThunk =>
  async () => {
    try {
      await axios.put(`/scooters/${id}/signal`, body);
    } catch (e: any) {
      handleDefaultErrors(e);
    }
  };

export const deleteScooterAsync =
  (id: string): AppThunk =>
  async () => {
    try {
      await axios.delete(`/scooters/${id}`);
    } catch (e: any) {
      handleDefaultErrors(e);
    }
  };

export const formatCDScooterAsync =
  (id: string): AppThunk =>
  async () => {
    try {
      await axios.put(`/scooters/${id}/reset_device`);
    } catch (e: any) {
      handleDefaultErrors(e);
    }
  };

export const getScooterNotesAsync = async (params?: GetScooterNotesParams) => {
  try {
    const response = await axios.get('/scooters/note', { params });
    return response.data;
  } catch (e: any) {
    handleDefaultErrors(e);
  }
};

export const addScooterNoteAsync = async (params?: AddScooterNoteParams) => {
  try {
    const response = await axios.post('/scooters/note', params);
    return response.data;
  } catch (e: any) {
    handleDefaultErrors(e);
  }
};

export default scootersSlice.reducer;
